/**
 * "Parts on this unit" card (SX-238 - forward component lens).
 *
 * Given a unit's serial, the platform resolves the physical components and
 * processes present on it (via batch cutover deviations authored per product
 * serial range) and returns them as ``holding.part_links``. This card renders
 * that list so a field engineer sees exactly what is fitted before servicing.
 *
 * Gating is entity/attribute PRESENCE, never a tenant slug or feature flag:
 *
 *   - ``<IfEntityAttribute name="part"       - the tenant has a ``part`` entity
 *       attribute="part_kind">``               that models ``part_kind`` (the
 *                                             SX-238 parts schema). The bare
 *                                             ``part`` key is NOT enough - it is
 *                                             not exclusive to this feature (a
 *                                             tenant may already use ``part`` for
 *                                             an unrelated catalogue that has no
 *                                             ``part_kind``), so we gate on the
 *                                             discriminating attribute the card
 *                                             actually renders.
 *   - ``<IfEntityAttribute name="holding"    - and has seeded the ``serial_number``
 *       attribute="serial_number">``           holding attribute (the precondition
 *                                             for resolving deviations by serial).
 *
 * Any other tenant opts in simply by seeding the SX-238 ``part`` schema (with
 * ``part_kind``) and a ``serial_number`` holding attribute - no code change. The
 * card renders nothing when either gate is absent, or when the unit carries no
 * parts. The backend reverse facet (``part_entity_for_org``) gates on the same
 * ``part_kind`` attribute, so both surfaces appear together.
 */

import { useTranslation } from "react-i18next";
import { Settings2, Cog } from "lucide-react";

import { IfEntityAttribute } from "@/admin/components/conditional-display";
import { DetailCard } from "@/admin/components/shared/detail-card";
import { Badge } from "@/shared/components/ui/badge";
import type { HoldingPartLink } from "@/admin/types/products";

/** Process treatments read as a calibration/coating; components read as a part. */
const PROCESS_KIND = "process";

function PartRow({ link }: { link: HoldingPartLink }) {
  const { t } = useTranslation();
  const isProcess = link.part_kind === PROCESS_KIND;
  return (
    <div className="flex items-center justify-between gap-3 rounded-[6px] border-[0.5px] border-[var(--rule)] bg-[var(--bg)] px-3.5 py-3">
      <div className="flex min-w-0 items-center gap-2.5">
        {isProcess ? (
          <Settings2 className="h-3.5 w-3.5 shrink-0 text-[var(--ink-3)]" />
        ) : (
          <Cog className="h-3.5 w-3.5 shrink-0 text-[var(--ink-3)]" />
        )}
        <span className="truncate text-[13px] font-normal text-[var(--ink)]">
          {link.part_label ||
            t("registrationDetail.parts.unnamed", "Unnamed part")}
        </span>
      </div>
      <Badge variant={isProcess ? "secondary" : "outline"} className="shrink-0">
        {isProcess
          ? t("registrationDetail.parts.process", "Process")
          : t("registrationDetail.parts.component", "Component")}
      </Badge>
    </div>
  );
}

export function PartsOnUnitCard({ links }: { links: HoldingPartLink[] }) {
  const { t } = useTranslation();

  // Presence gate: only tenants whose ``part`` entity models ``part_kind`` (the
  // SX-238 parts schema) AND that have a seeded ``serial_number`` holding
  // attribute see this card. Gating on ``part_kind`` (not the bare ``part``
  // key) keeps the card off tenants that use ``part`` for an unrelated
  // catalogue - no feature flag, no ``tenant.slug``.
  return (
    <IfEntityAttribute name="part" attribute="part_kind">
      <IfEntityAttribute name="holding" attribute="serial_number">
        <div id="parts-card">
          <DetailCard
            title={t("registrationDetail.parts.title", "Parts on this unit")}
            icon={<Cog className="h-3.5 w-3.5" />}
          >
            {links.length === 0 ? (
              <p className="py-4 text-center text-[12px] text-[var(--ink-3)]">
                {t(
                  "registrationDetail.parts.empty",
                  "No components or processes recorded for this serial.",
                )}
              </p>
            ) : (
              <div className="flex flex-col gap-2">
                {links.map((link) => (
                  <PartRow
                    key={`${link.part_record_uuid}:${link.part_slot}`}
                    link={link}
                  />
                ))}
              </div>
            )}
          </DetailCard>
        </div>
      </IfEntityAttribute>
    </IfEntityAttribute>
  );
}
